import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, ReservationSummary, Pagination, SearchBar, BackButton } from '../../../components/common';
import VerificationCodeModal from '../../../components/service/VerificationCodeModal';
import { reservationService } from '../../../api/services';
import client from '../../../api/client';
import { useAuth } from '../../../contexts/AuthContext';
import { useMoney } from '../../../hooks/useMoney';
import { formatReservationDate, formatDateTimeLocale } from '../../../utils/formatDate';
import { getBadgeServiceType } from '../../../constants/serviceLabels';

function makeMappers(t) {
  const SERVICE_LABELS = {
    airport: t('nav.private_transfer'), golf: t('nav.private_transfer'), ski: t('nav.private_transfer'),
    hospital: t('nav.private_transfer'), pet: t('nav.private_transfer'), business: t('nav.private_transfer'),
    tour: t('nav.private_tour'), shopping: t('nav.private_shopping'),
  };
  const SERVICE_TYPE_KR = {
    airport: t('service.airport'), golf: t('service.golf'), ski: t('service.ski'),
    hospital: t('service.hospital'), pet: t('service.pet'), business: t('service.business'),
    tour: t('service.tour'), shopping: t('service.shopping'),
  };
  const STATUS_LABELS = {
    pending: t('reservation.pending'), confirmed: t('reservation.confirmed'),
    driver_arrived: t('reservation.driver_arrived'), driving: t('reservation.driving'),
    drive_completed: t('reservation.drive_completed'), completed: t('reservation.completed'),
    cancelled: t('reservation.cancelled'), refunded: t('reservation.refunded'), no_show: t('reservation.no_show'),
  };
  return { SERVICE_LABELS, SERVICE_TYPE_KR, STATUS_LABELS };
}

function mapReservation(r, t) {
  const { SERVICE_LABELS, SERVICE_TYPE_KR, STATUS_LABELS } = makeMappers(t);
  // #17: list 와 lookup endpoint 의 응답 schema 가 달라 fallback 처리.
  //   - /api/reservations (list): top-level reservation_date / start_time / destination
  //   - /api/reservations/lookup (단건): route.reservation_date / route.departure.time / route.destination.address 중첩
  const reservationDate = r.reservation_date || r.route?.reservation_date;
  const startTime = r.start_time || r.route?.departure?.time;
  const destination = r.destination || r.route?.destination?.address || '';
  // BugHerd #1: 투어는 BE 가 Translation 적용된 tour_name 을 제공 (없으면 destination 폴백).
  // destination 은 itinerary 마지막 step 의 한국어 텍스트라 다국어 UI 에서 노출되면 안 됨.
  const tourTitle = r.tour_name || destination || t('nav.private_tour');
  return {
    id: r.id,
    service: SERVICE_LABELS[r.service_type] || t('nav.private_transfer'),
    serviceType: getBadgeServiceType(r.service_type),
    status: STATUS_LABELS[r.status] || r.status,
    paymentDate: r.created_at ? formatDateTimeLocale(r.created_at) + ' ' + t('complete.reservation_paid') : '-',
    title: r.service_type === 'tour'
      ? tourTitle
      : t('reservation.private_transfer_dash', { type: SERVICE_TYPE_KR[destination] || SERVICE_TYPE_KR[r.service_type] || destination || '' }),
    reservationNo: r.reservation_number,
    scheduledDate: formatReservationDate(reservationDate, startTime),
    amount: r.total_price || 0,
  };
}

export default function GuestReservation() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // 로그인 상태에서는 비회원 예약조회 직접 접근 차단 → 회원 예약 목록(/reservation)으로.
  // 단 이메일 "내 예약 바로 보기" 토큰 링크(?n=&t=)는 직접 단건 조회 허용.
  const blockGuestForMember = !authLoading && !!user && !(searchParams.get('n') && searchParams.get('t'));
  useEffect(() => {
    if (blockGuestForMember) navigate('/reservation', { replace: true });
  }, [blockGuestForMember, navigate]);
  const [searchMode, setSearchMode] = useState('email'); // 'email' | 'reservationNo'
  const [email, setEmail] = useState('');
  const [reservationNo, setReservationNo] = useState('');
  const [contactInfo, setContactInfo] = useState(''); // 이메일 또는 전화번호
  const [emailError, setEmailError] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verified, setVerified] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [results, setResults] = useState([]);
  const [guestToken, setGuestToken] = useState('');

  // #17 Flow B: 예약확정 메일의 "내 예약 바로 보기" 링크에서 진입한 경우
  //   ?n=VVT-XXXX&t=<signed_token> 으로 인증 모달 스킵하고 바로 단건 조회
  useEffect(() => {
    const n = searchParams.get('n');
    const tk = searchParams.get('t');
    if (!n || !tk) return;
    client.get('/api/reservations/lookup/by-token', { params: { reservation_number: n, token: tk } })
      .then((res) => {
        const item = res.data;
        // 이메일 링크(예약 상세 보기/영수증 보기)는 리스트 카드를 거치지 않고 곧바로 예약 상세로 이동.
        //   · guestEmail(booker_email)을 넘겨 비회원도 상세 조회가 인증되게 한다.
        //   · replace:true 로 token URL 을 상세 경로로 대체(토큰 노출 최소화).
        //   · 로그인 회원이 여기 머물면 파라미터 클린업 후 blockGuestForMember 로 /reservation 리스트로
        //     튕기던 문제도 함께 해결됨(상세로 먼저 빠져나감).
        navigate(`/reservation/${item.id}`, { replace: true, state: { guestEmail: item.booker_email } });
      })
      .catch((err) => {
        console.warn('[GuestReservation] token lookup 실패:', err.message);
        setEmailError(t('reservation.guest_lookup_not_found'));
        setSearchParams({}, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 시 조회 결과 복원
  useEffect(() => {
    const saved = sessionStorage.getItem('guestReservation');
    if (saved) {
      const { email: savedEmail, results: savedResults, token: savedToken, totalPages: savedTotalPages } = JSON.parse(saved);
      setEmail(savedEmail);
      setResults(savedResults);
      setGuestToken(savedToken || '');
      setTotalPages(savedTotalPages || 1);
      setVerified(true);
      if (savedToken) {
        localStorage.setItem('guestAccessToken', savedToken);
      }
    }
  }, []);

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError('');
    if (verified) {
      setVerified(false);
      setResults([]);
      setGuestToken('');
      setTotalPages(1);
      sessionStorage.removeItem('guestReservation');
    }
  };

  const handleSearch = async () => {
    const input = email.trim();
    if (!input) {
      setEmailError(t('reservation.guest_lookup_no_input'));
      return;
    }
    setEmailError('');

    // 예약번호 형식 (VVT- 로 시작)
    if (input.toUpperCase().startsWith('VVT-')) {
      try {
        const data = await reservationService.lookup({ reservation_number: input });
        if (data) {
          setResults(Array.isArray(data) ? data.map((r) => mapReservation(r, t)) : [mapReservation(data, t)]);
          setVerified(true);
        } else {
          setResults([]);
          setVerified(true);
        }
      } catch {
        setEmailError(t('reservation.guest_lookup_not_found'));
      }
      return;
    }

    // 이메일 형식
    if (validateEmail(input)) {
      setShowVerification(true);
      return;
    }

    setEmailError(t('reservation.guest_lookup_invalid'));
  };

  const fetchGuestPage = async (page, token) => {
    const activeToken = token || guestToken;
    if (!activeToken) return;
    // guest token을 별도 키에 저장하여 회원 세션을 보호
    const prevToken = localStorage.getItem('accessToken');
    localStorage.setItem('guestAccessToken', activeToken);
    localStorage.setItem('accessToken', activeToken);
    try {
      const data = await reservationService.getList({ page, size: 10 });
      const pageItems = (data.items || data || []).map((r) => mapReservation(r, t));
      setResults(pageItems);
      setTotalPages(data.pages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.warn('비회원 예약 조회 실패:', err.message);
      setResults([]);
      alert(t('reservation.guest_lookup_failed'));
    } finally {
      // 회원 토큰이 있었으면 복원
      if (prevToken && prevToken !== activeToken) {
        localStorage.setItem('accessToken', prevToken);
      }
      localStorage.removeItem('guestAccessToken');
    }
  };

  const handleVerified = async (token) => {
    setShowVerification(false);
    setVerified(true);
    setGuestToken(token);
    const prevToken = localStorage.getItem('accessToken');
    localStorage.setItem('guestAccessToken', token);
    localStorage.setItem('accessToken', token);
    try {
      const data = await reservationService.getList({ page: 1, size: 10 });
      const pageItems = (data.items || data || []).map((r) => mapReservation(r, t));
      setResults(pageItems);
      setTotalPages(data.pages || 1);
      setCurrentPage(1);
      sessionStorage.setItem('guestReservation', JSON.stringify({
        email: email.trim(), results: pageItems, token, totalPages: data.pages || 1,
      }));
    } catch (err) {
      console.warn('비회원 예약 조회 실패:', err.message);
      setResults([]);
      alert(t('reservation.guest_lookup_failed'));
    } finally {
      if (prevToken) {
        localStorage.setItem('accessToken', prevToken);
      }
      // prevToken 없으면 guest token 유지 (상세 페이지 이동 시 필요)
    }
  };

  // 로그인 회원이면 비회원 조회 UI 노출 없이 리다이렉트(깜빡임 방지).
  if (blockGuestForMember) return null;

  return (
    <div className="bg-mono-50 min-h-screen">
      {/* Sub-header bar: 비회원 예약 조회 (TabNav fullwidth 동일 구조) */}
      <div className="bg-white border-t border-mono-100">
        <div className="max-w-content mx-auto flex">
          <div className="flex-1 h-12 text-sm font-medium text-mono-900 flex items-center justify-center relative">
            {t('reservation.guest_lookup_title')}
            <span className="absolute bottom-0 left-0 right-0 h-px bg-orange-500" />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-content mx-auto px-2.5 sm:px-0 py-6 sm:py-8 space-y-4">
        <div className="flex items-center"><BackButton /></div>
        {/* 검색바 — 예약번호, 이메일, 전화번호 통합 검색 */}
        <SearchBar
          placeholder={t('reservation.guest_lookup_placeholder')}
          value={email}
          onChange={handleEmailChange}
          onSubmit={handleSearch}
          buttonLabel={t('reservation.guest_lookup_button')}
          error={emailError}
        />
        {verified ? (
          <>
            <div className="space-y-4">
              {results.map((item) => (
                <Card
                  key={item.id}
                  variant="padded"
                  size="lg"
                  interactive
                  onClick={() => navigate(`/reservation/${item.id}`, { state: { guestEmail: contactInfo } })}
                >
                  <ReservationSummary
                    service={item.service}
                    serviceType={item.serviceType}
                    status={item.status}
                    paymentDate={item.paymentDate}
                    title={item.title}
                    reservationNo={item.reservationNo}
                    scheduledDate={item.scheduledDate}
                  />
                  <div className="flex items-center justify-between pt-3 border-t border-mono-100">
                    <span className="text-sm font-bold text-mono-900">{t('reservation.total_amount')}</span>
                    <p className="flex items-baseline gap-0.5">
                      <span className="text-xl font-bold text-mono-900">{money(item.amount)}</span>
                    </p>
                  </div>
                </Card>
              ))}

              {results.length === 0 && (
                <div className="py-16 text-center">
                  <p className="text-sm text-mono-400">{t('reservation.guest_lookup_no_result')}</p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <Pagination
                current={currentPage}
                total={totalPages}
                onChange={(page) => fetchGuestPage(page)}
                className="mt-8"
              />
            )}
          </>
        ) : (
          <div className="py-16 text-center">
            <p className="text-sm text-mono-400">{t('reservation.guest_lookup_search_email')}</p>
          </div>
        )}
      </div>

      {/* Verification Code Modal */}
      {showVerification && (
        <VerificationCodeModal
          email={email}
          onClose={() => setShowVerification(false)}
          onVerified={handleVerified}
        />
      )}
    </div>
  );
}
